import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import './Messages.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const socket = useSocket();
  const navigate = useNavigate();

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!socket) return;
    const handleRefresh = () => fetchConversations();
    socket.on('new_message', handleRefresh);
    socket.on('messages_read', handleRefresh);
    return () => {
      socket.off('new_message', handleRefresh);
      socket.off('messages_read', handleRefresh);
    };
  }, [socket, fetchConversations]);

  const getTimeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return new Date(date).toLocaleDateString();
  };

  if (loading) return <div className="loader-container"><i className="fa-solid fa-spinner fa-spin"></i></div>;

  return (
    <div className="msg-wrapper">
      <div className="msg-header">
        <div className="msg-header-text">
          <h1><i className="fa-regular fa-comments"></i> Messages</h1>
          <p>Communicate directly with your partners</p>
        </div>
      </div>

      <div className="msg-list-container">
        {conversations.length === 0 ? (
          <div className="msg-empty">
            <i className="fa-solid fa-inbox"></i>
            <p>You have no active conversations.</p>
          </div>
        ) : (
          conversations.map(conv => (
            <div 
              key={conv.userId} 
              className={`msg-item ${conv.unreadCount > 0 ? 'unread' : ''}`} 
              onClick={() => navigate(`/messages/${conv.userId}`, { state: { userName: conv.userName } })}
            >
              <div className="msg-item-left">
                <div className="msg-avatar">
                  {conv.userName?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="msg-info">
                  <span className="msg-name">{conv.userName}</span>
                  <span className="msg-preview">{conv.lastMessage || 'Sent a message'}</span>
                </div>
              </div>
              <div className="msg-item-right">
                <span className="msg-time">{getTimeAgo(conv.lastMessageTime)}</span>
                {conv.unreadCount > 0 && <span className="msg-unread-dot">{conv.unreadCount} New</span>}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Messages;